/**
 * SPA helpers · session list + create
 *
 * Backend真源：`apps/backend/src/routes/sessions-chat.ts`
 *   GET  /api/sessions          → { sessions: SessionRow[] }
 *   POST /api/sessions { title? } → SessionRow (201)
 */

export interface SessionRow {
  session_id: string;
  title: string | null;
  status: 'active' | 'paused' | 'archived';
  created_at: number;
  archived_at?: number | null;
  review_enabled?: number;
  workflow_plan_revision?: number;
  workflow_planning_enabled?: number;
}

export async function fetchSessions(
  backendUrl: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SessionRow[]> {
  const url = `${backendUrl.replace(/\/$/, '')}/api/sessions`;
  const resp = await fetchImpl(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    throw new Error(`sessions list fetch failed: ${resp.status}`);
  }
  const body = await resp.json() as { sessions?: unknown };
  if (!Array.isArray(body.sessions)) {
    throw new Error('sessions list returned malformed body');
  }
  return body.sessions as SessionRow[];
}

export async function createSession(
  backendUrl: string,
  token: string,
  title: string | undefined,
  fetchImpl: typeof fetch = fetch,
): Promise<SessionRow> {
  const url = `${backendUrl.replace(/\/$/, '')}/api/sessions`;
  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(title ? { title } : {}),
  });
  if (resp.status !== 201) {
    const detail = await resp.text().catch(() => '<unreadable>');
    throw new Error(`session create failed: ${resp.status} · ${detail.slice(0, 200)}`);
  }
  const row = await resp.json() as Partial<SessionRow>;
  if (typeof row.session_id !== 'string') {
    throw new Error('session create returned malformed body');
  }
  return row as SessionRow;
}

export async function updateSessionReviewEnabled(
  backendUrl: string,
  token: string,
  sessionId: string,
  reviewEnabled: boolean,
  fetchImpl: typeof fetch = fetch,
): Promise<{ ok: true; review_enabled: number }> {
  const url = `${backendUrl.replace(/\/$/, '')}/api/sessions/${encodeURIComponent(sessionId)}/review-enabled`;
  const resp = await fetchImpl(url, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ review_enabled: reviewEnabled }),
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '<unreadable>');
    throw new Error(`session review toggle failed: ${resp.status} · ${detail.slice(0, 200)}`);
  }
  const body = await resp.json() as { ok?: boolean; review_enabled?: number };
  if (body.ok !== true || typeof body.review_enabled !== 'number') {
    throw new Error('session review toggle returned malformed body');
  }
  return { ok: true, review_enabled: body.review_enabled };
}
